/**
 * Extracted Positions List Component
 *
 * T096 [US5]: Display AI-extracted positions/trades in editable list format.
 * Shows confidence scores and allows inline editing.
 * NOW THEME-AWARE
 */

import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TextInput, TouchableOpacity } from 'react-native';
import { useTranslation } from 'react-i18next';
import type { ExtractedPosition, ExtractedTrade } from '../../services/api/documentImportApi';
import { useTheme } from '../../contexts/ThemeContext';
import { SymbolAutocomplete, type SymbolOption } from '../portfolio/SymbolAutocomplete';

type ExtractedItem = ExtractedPosition | ExtractedTrade;

interface ExtractedPositionsListProps {
  positions: ExtractedItem[];
  onPositionsChange: (positions: ExtractedItem[]) => void;
}

function isTrade(item: ExtractedItem): item is ExtractedTrade {
  return 'price' in item;
}

function hasCommission(item: ExtractedItem): item is ExtractedTrade & { commission_fee: number } {
  return isTrade(item) && item.commission_fee !== undefined && item.commission_fee !== null;
}

export function ExtractedPositionsList({
  positions,
  onPositionsChange,
}: ExtractedPositionsListProps) {
  const { theme } = useTheme();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const { t } = useTranslation();

  const updatePosition = (index: number, field: string, value: string | number) => {
    const updated = [...positions];
    updated[index] = { ...updated[index], [field]: value };
    onPositionsChange(updated);
  };

  /**
   * Raw-text mirror for the numeric fields.
   *
   * The parent (DocumentReviewScreen) keeps these fields as NUMBERS — validation,
   * duplicate detection, and the save API all rely on that. But a controlled
   * TextInput whose `value` is derived from a number and whose `onChangeText`
   * writes `parseFloat(value) || 0` round-trips string -> number -> toString on
   * every keystroke, which destroys in-progress decimal input: `parseFloat('150.')`
   * is `150`, so the decimal separator collapses back to "150" on each re-render
   * and the user can never type a decimal price.
   *
   * Fix: keep the raw edited string here for display, and lift only the parsed
   * number upstream. On blur we drop the raw entry so the display reverts to the
   * canonical number string (normalising a dangling ".") and the mirror stays
   * correct if the list is reordered or an item is removed.
   */
  const [rawText, setRawText] = useState<Record<string, string>>({});
  const fieldKey = (index: number, field: string) => `${index}:${field}`;

  const setRaw = (index: number, field: string, value: string) =>
    setRawText((prev) => ({ ...prev, [fieldKey(index, field)]: value }));

  const clearRaw = (index: number, field: string) =>
    setRawText((prev) => {
      const key = fieldKey(index, field);
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

  /** Display the raw edited text if present, otherwise the number's string form. */
  const rawOr = (index: number, field: string, fallback: string) =>
    rawText[fieldKey(index, field)] ?? fallback;

  /** Store the raw text locally and lift the parsed number to the parent. */
  const liftNumber = (
    index: number,
    field: string,
    value: string,
    toNumber: (n: number) => number = (n) => n
  ) => {
    setRaw(index, field, value);
    updatePosition(index, field, toNumber(parseFloat(value) || 0));
  };

  const handleSymbolSelect = (index: number, option: SymbolOption) => {
    const updated = [...positions];
    updated[index] = {
      ...updated[index],
      symbol: option.symbol,
      // Update currency if the selected option has one and the field exists in the item type
      ...(option.currency && { currency: option.currency }),
      // Only treat this as an explicit instrument choice when the option carries
      // an instrument id (a real pick from the instruments search). SymbolAutocomplete
      // also fires onSymbolSelect on mount to sync the initial value with NO id — in
      // that case we must NOT clobber the auto-resolved link that /extracted set
      // (instrument_id/instrument_name), or every render would wipe the auto-resolution.
      ...(option.id !== undefined && {
        instrument_id: option.id,
        instrument_name: option.name,
        // Drop the previously-linked instrument's localized names so they don't
        // leak through to /save or the review display.
        instrument_name_sch: undefined,
        instrument_name_tch: undefined,
      }),
    };
    onPositionsChange(updated);
  };

  const handleSymbolTextChange = (index: number, value: string) => {
    const updated = [...positions];
    updated[index] = {
      ...updated[index],
      symbol: value.toUpperCase(),
      // The symbol no longer matches the linked instrument. Drop the stale link so
      // /save re-resolves from symbol+currency instead of trusting the previous
      // auto-resolution (and so the review display stops showing the old instrument).
      instrument_id: undefined,
      instrument_name: undefined,
      instrument_name_sch: undefined,
      instrument_name_tch: undefined,
    };
    onPositionsChange(updated);
  };

  const removePosition = (index: number) => {
    const updated = positions.filter((_, i) => i !== index);
    onPositionsChange(updated);
  };

  const handleAddAction = (index: number) => {
    updatePosition(index, 'resolution_action', 'add');
  };

  const handleReplaceAction = (index: number) => {
    updatePosition(index, 'resolution_action', 'replace');
  };

  const getConfidenceColor = (confidence?: number) => {
    if (!confidence) return theme.colors.textTertiary;
    if (confidence >= 0.9) return theme.colors.success; // Green
    if (confidence >= 0.7) return theme.colors.warning || '#ff9800'; // Orange
    return theme.colors.error; // Red
  };

  const getConfidenceLabel = (confidence?: number) => {
    if (!confidence) return 'Unknown';
    if (confidence >= 0.9) return 'High';
    if (confidence >= 0.7) return 'Medium';
    return 'Low';
  };

  const getTradeSide = (quantity: number) => {
    return quantity >= 0 ? 'BUY' : 'SELL';
  };

  /**
   * Compute the reconciling trade preview for a position:
   * delta = snapshot quantity - existing trade-derived quantity (or full
   * snapshot quantity when the user chose "add"). Returns null when no trade
   * is needed.
   */
  const getReconcilePreview = (position: ExtractedPosition) => {
    const snapshotQty = Number(position.quantity) || 0;
    const existingQty = Number(position.existing_trade_quantity) || 0;
    const net = position.resolution_action === 'add' ? snapshotQty : snapshotQty - existingQty;
    if (Math.abs(net) <= 0.0001) return null;
    const increase = net > 0;
    return {
      net,
      magnitude: Math.abs(net),
      existingQty,
      snapshotQty,
      defaultType: increase ? 'deposit' : 'withdrawal',
      altType: increase ? 'buy' : 'sell',
      increase,
    };
  };

  /** Format a quantity/price for display: strip float noise, no trailing zeros. */
  const fmtNum = (n: number) => String(Math.round(n * 1e6) / 1e6);

  return (
    <View style={styles.container}>
      {positions.map((position, index) => (
        <View
          key={index}
          style={[
            styles.card,
            { backgroundColor: theme.colors.surface, borderColor: theme.colors.border },
          ]}
        >
          {/* Header with confidence badge and duplicate actions */}
          <View style={styles.cardHeader}>
            <Text style={[styles.cardTitle, { color: theme.colors.text }]}>
              {isTrade(position) ? `Trade ${index + 1}` : `Position ${index + 1}`}
            </Text>
            <View style={styles.badgeContainer}>
              {/* Duplicate badge for trades (just warning, no resolution needed) */}
              {position.is_duplicate && isTrade(position) && (
                <View
                  style={[
                    styles.duplicateBadge,
                    { backgroundColor: theme.colors.warning || '#ff9800' },
                  ]}
                >
                  <Text style={[styles.badgeText, { color: theme.colors.textInverse }]}>
                    DUPLICATE
                  </Text>
                </View>
              )}
              {/* Duplicate resolution buttons/badge for positions */}
              {position.is_duplicate && !isTrade(position) && !position.resolution_action && (
                <View style={styles.duplicateButtonsContainer}>
                  <TouchableOpacity
                    onPress={() => handleAddAction(index)}
                    style={[
                      styles.duplicateActionButton,
                      { backgroundColor: theme.colors.success },
                    ]}
                  >
                    <Text style={[styles.duplicateActionText, { color: theme.colors.textInverse }]}>
                      Add
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => handleReplaceAction(index)}
                    style={[styles.duplicateActionButton, { backgroundColor: theme.colors.error }]}
                  >
                    <Text style={[styles.duplicateActionText, { color: theme.colors.textInverse }]}>
                      Replace
                    </Text>
                  </TouchableOpacity>
                </View>
              )}
              {position.is_duplicate && !isTrade(position) && position.resolution_action && (
                <View style={[styles.duplicateBadge, { backgroundColor: theme.colors.success }]}>
                  <Text style={[styles.badgeText, { color: theme.colors.textInverse }]}>
                    {position.resolution_action === 'add' ? 'ADD' : 'REPLACE'}
                  </Text>
                </View>
              )}
              {position.confidence !== undefined && (
                <View
                  style={[
                    styles.confidenceBadge,
                    { backgroundColor: getConfidenceColor(position.confidence) },
                  ]}
                >
                  <Text style={[styles.confidenceText, { color: theme.colors.textInverse }]}>
                    {getConfidenceLabel(position.confidence)} (
                    {(position.confidence * 100).toFixed(0)}%)
                  </Text>
                </View>
              )}
            </View>
          </View>

          {/* Common fields */}
          <View style={styles.fieldContainer}>
            <Text style={[styles.label, { color: theme.colors.textSecondary }]}>Symbol</Text>
            <SymbolAutocomplete
              value={position.symbol}
              onChangeText={(value) => handleSymbolTextChange(index, value)}
              onSymbolSelect={(option) => handleSymbolSelect(index, option)}
              placeholder="e.g., AAPL"
              autoFilled={true}
              instrumentName={position.instrument_name}
            />
          </View>

          {isTrade(position) ? (
            <>
              {/* Trade-specific fields */}
              <View style={styles.fieldRow}>
                <View style={styles.fieldContainer}>
                  <Text style={[styles.label, { color: theme.colors.textSecondary }]}>Side</Text>
                  <View
                    style={[
                      styles.input,
                      styles.readOnly,
                      {
                        backgroundColor: theme.colors.backgroundElevated,
                        borderColor: theme.colors.border,
                      },
                    ]}
                  >
                    <Text style={[styles.readOnlyText, { color: theme.colors.text }]}>
                      {getTradeSide(position.quantity)}
                    </Text>
                  </View>
                </View>

                <View style={styles.fieldContainer}>
                  <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                    Quantity
                  </Text>
                  <TextInput
                    style={[
                      styles.input,
                      {
                        backgroundColor: theme.colors.backgroundElevated,
                        borderColor: theme.colors.border,
                        color: theme.colors.text,
                      },
                    ]}
                    value={rawOr(index, 'quantity', Math.abs(position.quantity).toString())}
                    onChangeText={(value) => {
                      const currentSide = getTradeSide(position.quantity);
                      liftNumber(index, 'quantity', value, (n) =>
                        currentSide === 'SELL' ? -n : n
                      );
                    }}
                    onBlur={() => clearRaw(index, 'quantity')}
                    placeholder="50"
                    placeholderTextColor={theme.colors.textTertiary}
                    keyboardType="decimal-pad"
                  />
                </View>
              </View>

              <View style={styles.fieldRow}>
                <View style={styles.fieldContainer}>
                  <Text style={[styles.label, { color: theme.colors.textSecondary }]}>Price</Text>
                  <TextInput
                    style={[
                      styles.input,
                      {
                        backgroundColor: theme.colors.backgroundElevated,
                        borderColor: theme.colors.border,
                        color: theme.colors.text,
                      },
                    ]}
                    value={rawOr(index, 'price', position.price?.toString() || '')}
                    onChangeText={(value) => liftNumber(index, 'price', value)}
                    onBlur={() => clearRaw(index, 'price')}
                    placeholder="150.00"
                    placeholderTextColor={theme.colors.textTertiary}
                    keyboardType="decimal-pad"
                  />
                </View>

                <View style={styles.fieldContainer}>
                  <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                    Currency
                  </Text>
                  <TextInput
                    style={[
                      styles.input,
                      {
                        backgroundColor: theme.colors.backgroundElevated,
                        borderColor: theme.colors.border,
                        color: theme.colors.text,
                      },
                    ]}
                    value={position.currency}
                    onChangeText={(value) => updatePosition(index, 'currency', value.toUpperCase())}
                    placeholder="USD"
                    placeholderTextColor={theme.colors.textTertiary}
                    autoCapitalize="characters"
                  />
                </View>
              </View>

              {/* Optional trade fields */}
              {position.commission_fee !== undefined && (
                <View style={styles.fieldContainer}>
                  <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                    Commission
                  </Text>
                  <TextInput
                    style={[
                      styles.input,
                      {
                        backgroundColor: theme.colors.backgroundElevated,
                        borderColor: theme.colors.border,
                        color: theme.colors.text,
                      },
                    ]}
                    value={rawOr(
                      index,
                      'commission_fee',
                      position.commission_fee?.toString() || ''
                    )}
                    onChangeText={(value) => liftNumber(index, 'commission_fee', value)}
                    onBlur={() => clearRaw(index, 'commission_fee')}
                    placeholder="1.50"
                    placeholderTextColor={theme.colors.textTertiary}
                    keyboardType="decimal-pad"
                  />
                </View>
              )}

              <View style={styles.fieldContainer}>
                <Text style={[styles.label, { color: theme.colors.textSecondary }]}>Date</Text>
                <TextInput
                  style={[
                    styles.input,
                    {
                      backgroundColor: theme.colors.backgroundElevated,
                      borderColor: theme.colors.border,
                      color: theme.colors.text,
                    },
                  ]}
                  value={position.date}
                  onChangeText={(value) => updatePosition(index, 'date', value)}
                  placeholder="YYYY-MM-DD"
                  placeholderTextColor={theme.colors.textTertiary}
                />
              </View>
            </>
          ) : (
            <>
              {/* Position-specific fields */}
              <View style={styles.fieldRow}>
                <View style={styles.fieldContainer}>
                  <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                    Quantity
                  </Text>
                  <TextInput
                    style={[
                      styles.input,
                      {
                        backgroundColor: theme.colors.backgroundElevated,
                        borderColor: theme.colors.border,
                        color: theme.colors.text,
                      },
                    ]}
                    value={rawOr(index, 'quantity', position.quantity.toString())}
                    onChangeText={(value) => liftNumber(index, 'quantity', value)}
                    onBlur={() => clearRaw(index, 'quantity')}
                    placeholder="100"
                    placeholderTextColor={theme.colors.textTertiary}
                    keyboardType="decimal-pad"
                  />
                </View>

                <View style={styles.fieldContainer}>
                  <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                    Cost Price
                  </Text>
                  <TextInput
                    style={[
                      styles.input,
                      {
                        backgroundColor: theme.colors.backgroundElevated,
                        borderColor: theme.colors.border,
                        color: theme.colors.text,
                      },
                    ]}
                    value={rawOr(index, 'cost_price', position.cost_price?.toString() || '')}
                    onChangeText={(value) => liftNumber(index, 'cost_price', value)}
                    onBlur={() => clearRaw(index, 'cost_price')}
                    placeholder="150.00"
                    placeholderTextColor={theme.colors.textTertiary}
                    keyboardType="decimal-pad"
                  />
                </View>
              </View>

              <View style={styles.fieldRow}>
                <View style={styles.fieldContainer}>
                  <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                    Currency
                  </Text>
                  <TextInput
                    style={[
                      styles.input,
                      {
                        backgroundColor: theme.colors.backgroundElevated,
                        borderColor: theme.colors.border,
                        color: theme.colors.text,
                      },
                    ]}
                    value={position.currency}
                    onChangeText={(value) => updatePosition(index, 'currency', value.toUpperCase())}
                    placeholder="USD"
                    placeholderTextColor={theme.colors.textTertiary}
                    autoCapitalize="characters"
                  />
                </View>

                <View style={styles.fieldContainer}>
                  <Text style={[styles.label, { color: theme.colors.textSecondary }]}>Date</Text>
                  <TextInput
                    style={[
                      styles.input,
                      {
                        backgroundColor: theme.colors.backgroundElevated,
                        borderColor: theme.colors.border,
                        color: theme.colors.text,
                      },
                    ]}
                    value={position.date}
                    onChangeText={(value) => updatePosition(index, 'date', value)}
                    placeholder="YYYY-MM-DD"
                    placeholderTextColor={theme.colors.textTertiary}
                  />
                </View>
              </View>

              {/* Reconciling trade preview (import auto-reconcile feature) */}
              {(() => {
                const preview = getReconcilePreview(position as ExtractedPosition);
                if (!preview) return null;
                const selectedType =
                  (position as ExtractedPosition).reconcile_transaction_type || preview.defaultType;
                const cost = (position as ExtractedPosition).cost_price;
                return (
                  <View
                    style={[
                      styles.reconcileBox,
                      {
                        backgroundColor: theme.colors.warning + '15',
                        borderColor: theme.colors.warning || '#ff9800',
                      },
                    ]}
                  >
                    <Text style={[styles.reconcileText, { color: theme.colors.text }]}>
                      {t('portfolio.reconcilePreview', {
                        defaultValue:
                          "Snapshot shows {{snapshot}}, your trades show {{existing}}. We'll add a {{type}} of {{qty}}{{price}}.",
                        snapshot: fmtNum(preview.snapshotQty),
                        existing: fmtNum(preview.existingQty),
                        type: t(`portfolio.${selectedType}`, { defaultValue: selectedType }),
                        qty: fmtNum(preview.magnitude),
                        price: cost != null && Number(cost) > 0 ? ` @ ${fmtNum(Number(cost))}` : '',
                      })}
                    </Text>
                    <View style={styles.reconcileToggleRow}>
                      {[preview.defaultType, preview.altType].map((type) => (
                        <TouchableOpacity
                          key={type}
                          onPress={() => updatePosition(index, 'reconcile_transaction_type', type)}
                          style={[
                            styles.reconcileToggleButton,
                            {
                              backgroundColor:
                                selectedType === type
                                  ? theme.colors.primary
                                  : theme.colors.backgroundElevated,
                              borderColor:
                                selectedType === type ? theme.colors.primary : theme.colors.border,
                            },
                          ]}
                        >
                          <Text
                            style={[
                              styles.reconcileToggleText,
                              {
                                color:
                                  selectedType === type
                                    ? theme.colors.textInverse
                                    : theme.colors.text,
                              },
                            ]}
                          >
                            {t(`portfolio.${type}`, { defaultValue: type })}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                );
              })()}
            </>
          )}

          {/* Remove button */}
          <TouchableOpacity
            style={[styles.removeButton, { backgroundColor: theme.colors.error + '20' }]}
            onPress={() => removePosition(index)}
          >
            <Text style={[styles.removeButtonText, { color: theme.colors.error }]}>Remove</Text>
          </TouchableOpacity>
        </View>
      ))}

      {positions.length === 0 && (
        <View style={[styles.emptyState, { backgroundColor: theme.colors.backgroundElevated }]}>
          <Text style={[styles.emptyText, { color: theme.colors.textTertiary }]}>
            No positions to display
          </Text>
        </View>
      )}
    </View>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      gap: theme.spacing.md,
    },
    card: {
      borderRadius: theme.radii.md,
      padding: theme.spacing.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      zIndex: 1,
    },
    cardHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing.md,
    },
    cardTitle: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    badgeContainer: {
      flexDirection: 'row',
      gap: theme.spacing.sm,
      alignItems: 'center',
    },
    duplicateButtonsContainer: {
      flexDirection: 'row',
      gap: theme.spacing.xs,
      alignItems: 'center',
    },
    duplicateActionButton: {
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: theme.spacing.xs,
      borderRadius: theme.radii.sm,
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.textInverse,
      minWidth: 50,
      alignItems: 'center',
    },
    duplicateActionText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    confidenceBadge: {
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: theme.spacing.xs,
      borderRadius: theme.radii.full,
    },
    duplicateBadge: {
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: theme.spacing.xs,
      borderRadius: theme.radii.full,
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.textInverse,
    },
    badgeText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    confidenceText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    fieldContainer: {
      flex: 1,
      marginBottom: theme.spacing.md,
    },
    fieldRow: {
      flexDirection: 'row',
      gap: theme.spacing.md,
    },
    label: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightMedium,
      marginBottom: theme.spacing.xs,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? 0.5 : 0,
    },
    input: {
      borderRadius: theme.radii.sm,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
      fontSize: theme.typography.fontSizeSm,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    readOnly: {
      justifyContent: 'center',
    },
    readOnlyText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightMedium,
    },
    removeButton: {
      marginTop: theme.spacing.sm,
      paddingVertical: theme.spacing.sm,
      alignItems: 'center',
      borderRadius: theme.radii.sm,
    },
    removeButtonText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    emptyState: {
      padding: 24,
      alignItems: 'center',
      borderRadius: theme.radii.md,
    },
    emptyText: {
      fontSize: theme.typography.fontSizeSm,
    },
    reconcileBox: {
      padding: theme.spacing.sm,
      borderRadius: theme.radii.sm,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      marginBottom: theme.spacing.md,
      gap: theme.spacing.sm,
    },
    reconcileText: {
      fontSize: theme.typography.fontSizeXs,
    },
    reconcileToggleRow: {
      flexDirection: 'row',
      gap: theme.spacing.sm,
    },
    reconcileToggleButton: {
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs,
      borderRadius: theme.radii.sm,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    reconcileToggleText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
    },
  });
}
